import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { HelpCircle } from "@repo/icons";
import { formatCurrencyFromCentsToReais } from "@repo/lib";
import {
  useRouter,
  useSearch,
  useRouteContext,
  useNavigate,
} from "@tanstack/react-router";
import { CpsAlert } from "corpus";
import {
  PatientPaymentDetailsPageDocument,
  PatientsPaymentsQueryDocument,
} from "@repo/graphql-types/graphql";
import { Button } from "@/components/button";
import { GenericSuccessDrawer } from "@/components/generic-success-drawer";
import { UnexpectedErrorDrawer } from "@/components/unexpected-error-drawer";
import { useGraphQLMutationWithErrorHandler, useResetQuery } from "@/hooks/use-graphql";
import { trackEvent } from "@/lib/tracking";

export const RefundDetailsQueryFragment = graphql(`
  fragment RefundDetailsQueryFragment on query_root {
    ExpensumPaymentDetails(arg1: {onlinePaymentGuid: $onlinePaymentGuid, codAgendamento: $codAgendamento}) {
      acquirerTransactionKey
      amount
      paidAt
      origin
      paymentMethod
      payablesInfo {
        payables {
          fee
        }
      }
    }
    ExpensumGetRecipient(arg1: {codUsuario: $codUsuario, codClinica: $codClinica}) {
      recipient {
        pagarmeId
      }
    }
  }
`);

const PatientPaymentRefundPageMutation = graphql(`
  mutation PatientPaymentRefundMutation($acquirerTransactionKey: String!) {
    ExpensumChargeCancel(
      arg1: { acquirerTransactionKey: $acquirerTransactionKey }
    ) {
      charge {
        id
      }
    }
  }
`);

interface RefundDetailsProps {
  data: FragmentType<typeof RefundDetailsQueryFragment>;
}

export const RefundDetails = ({ data }: RefundDetailsProps): JSX.Element => {
  const router = useRouter();
  const navigate = useNavigate();

  const { user } = useRouteContext({ strict: false });

  const { guid, appointmentId, action } = useSearch({
    from: "/settings/payments/patient-payments/refund",
  });
  const invalidatePatientsPaymentsQuery = useResetQuery(PatientsPaymentsQueryDocument);
  const invalidatePatientsPaymentDetailsQuery = useResetQuery(
    PatientPaymentDetailsPageDocument,
    {
      onlinePaymentGuid: guid ?? undefined,
      codAgendamento: appointmentId ?? undefined,
    },
  );

  const refundData = useFragment(RefundDetailsQueryFragment, data);

  const payment = refundData.ExpensumPaymentDetails;
  const recipientData = refundData.ExpensumGetRecipient;

  const fee = formatCurrencyFromCentsToReais(payment?.payablesInfo.payables[0]?.fee ?? 0);

  const handleSuccessDrawerDismiss = (value: boolean): void => {
    if (!value) {
      invalidatePatientsPaymentsQuery();
      invalidatePatientsPaymentDetailsQuery();
      router.history.go(-2);
    }
  };

  const navigateRefund = (newAction?: "success" | "error"): void => {
    void navigate({
      search: {
        guid,
        appointmentId,
        action: newAction,
      },
      replace: true,
    });
  };

  const handleErrorDrawerDismiss = (value: boolean): void => {
    if (!value) {
      navigateRefund();
    }
  };

  const { mutateAsync, isPending } = useGraphQLMutationWithErrorHandler(
    PatientPaymentRefundPageMutation,
  );

  const handleSubmit = async (): Promise<void> => {
    const onSuccess = (): void => {
      trackEvent("Gerenciar Pagamentos Estorno Confirmado", {
        codUsuario: user.codUsuario,
        codClinica: user.codClinica,
        recebedor: recipientData?.recipient?.pagarmeId,
        origemPagamento: payment?.origin,
        onlinePaymentGuid: guid,
        codAgendamento: appointmentId,
        idTransacao: payment?.acquirerTransactionKey,
        valorEstornado: payment?.amount,
        formaPagamento: payment?.paymentMethod,
        dataPagamento: payment?.paidAt,
        dataSolicitacaoEstorno: new Date().toISOString(),
      });
      navigateRefund("success");
    };

    const onError = (): void => {
      navigateRefund("error");
    };

    await mutateAsync(
      {
        acquirerTransactionKey: String(payment?.acquirerTransactionKey),
      },
      {
        onSuccess,
        onError,
      },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-xl font-medium mb-2 text-left text-neutral-900">
        Deseja estornar esse pagamento?
      </h2>
      <p className="mb-4 text-base text-neutral-500 text-left">
        Confirme as informações antes de finalizar essa ação
      </p>
      <div className="flex items-center justify-between gap-2 w-full">
        <span className="text-neutral-500 text-base">Valor de estorno:</span>
        <span
          id="refund-amount"
          data-testid="refund-amount"
          className="font-bold text-xl tracking-wide"
        >
          {formatCurrencyFromCentsToReais(Number(payment?.amount))}
        </span>
      </div>

      {payment?.paymentMethod === "pix" && (
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="font-bold text-neutral-500 text-base">
            Taxa cobrada do saldo:
          </span>
          <span id="refund-fee" data-testid="refund-fee">
            {fee}
          </span>
        </div>
      )}
      <span className="text-sm text-left text-neutral-500 w-full mx-auto mb-4 block">
        {payment?.paymentMethod === "pix" ? (
          <>
            O favorecido receberá o valor total do estorno.{" "}
            <span className="font-bold">A taxa do Pix será descontada do seu saldo.</span>
          </>
        ) : (
          "O valor será estornado integralmente"
        )}
      </span>
      <CpsAlert
        Icon={HelpCircle}
        type="info"
        title="Caso não haja saldo disponível, o valor será descontado automaticamente da próxima transação"
      />
      <div className="flex flex-col gap-3 mt-1">
        <Button
          id="confirm-refund-button"
          data-testid="confirm-refund-button"
          onClick={() => {
            void handleSubmit();
          }}
          loading={isPending}
        >
          Confirmar estorno
        </Button>
      </div>
      <GenericSuccessDrawer
        open={action === "success"}
        title="Pagamento estornado!"
        setOpen={handleSuccessDrawerDismiss}
        confirmButtonText="Voltar"
      />
      <UnexpectedErrorDrawer
        open={action === "error"}
        setOpen={handleErrorDrawerDismiss}
      />
    </div>
  );
};
